/**
 * Validation for order confirmation CSV extraction review.
 */

import {
  getOrderConfirmationFormat,
  isSupportedOrderConfirmationFormat,
} from '../config/orderConfirmationFormats';
import { sumOrderConfirmationLineTotals } from './orderConfirmationMapping';

/**
 * @param {*} value
 * @returns {boolean}
 */
function isBlank(value) {
  return value == null || String(value).trim() === '';
}

/**
 * @param {*} value
 * @returns {number|null}
 */
function toNumber(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * @typedef {Object} OrderConfirmationValidationResult
 * @property {boolean} canSave
 * @property {string[]} blockingErrors
 * @property {import('../types/orderConfirmationExtractionTypes').ExtractionWarning[]} warnings
 */

/**
 * @param {import('../types/orderConfirmationExtractionTypes').ExtractedOrderConfirmationData} data
 * @param {{ supplierId?: string|number|null, supplierOptions?: Array, budget?: string, fundingCreditId?: string|number|null }} [context]
 * @returns {OrderConfirmationValidationResult}
 */
export function validateExtractedOrderConfirmationForSave(data, context = {}) {
  const blockingErrors = [];
  const warnings = [...(data.warnings || [])];

  const formatId = data.meta?.parserFormat;
  if (formatId && !isSupportedOrderConfirmationFormat(formatId)) {
    blockingErrors.push(
      `Order confirmation format “${formatId}” is not supported. Re-extract or contact an administrator.`
    );
  }

  const supplierId = context.supplierId;
  if (supplierId == null || String(supplierId).trim() === '') {
    blockingErrors.push('Supplier is required before saving.');
  }

  const hasBudgetLine =
    context.budgetLineId != null && String(context.budgetLineId).trim() !== '';
  const budgetText = String(context.budget ?? '').trim();
  const hasBudget = budgetText !== '';
  const hasFundingCredit = String(context.fundingCreditId ?? '').trim() !== '';
  if (!hasBudgetLine && !hasBudget && !hasFundingCredit) {
    blockingErrors.push('Either budget or supplier credit is required.');
  }

  const formatDef = formatId ? getOrderConfirmationFormat(formatId) : null;
  if (
    formatDef &&
    supplierId != null &&
    String(supplierId).trim() !== '' &&
    Array.isArray(context.supplierOptions)
  ) {
    const rematch = formatDef.matchMateriahSupplierOption(
      context.supplierOptions,
      data.supplier
    );
    if (
      rematch &&
      String(rematch.value) !== String(supplierId) &&
      rematch.score >= 55
    ) {
      warnings.push({
        field: 'supplier',
        message: `Selected supplier does not match the ${formatDef.displayName} name on the CSV. Expected a name like “${rematch.label}”.`,
        severity: 'warning',
      });
    }
  }

  if (isBlank(data.order?.orderNumber)) {
    blockingErrors.push('Order number is required.');
  }
  if (isBlank(data.order?.orderDate)) {
    blockingErrors.push('Order date is required.');
  }
  if (isBlank(data.order?.currency)) {
    blockingErrors.push('Currency is required.');
  }

  const items = data.items || [];
  if (items.length === 0) {
    blockingErrors.push(
      'No order lines were detected. You can add them manually before saving.'
    );
  }

  const totalPrice =
    toNumber(data.totals?.itemsSubtotal) ?? sumOrderConfirmationLineTotals(items);
  if (totalPrice <= 0) {
    blockingErrors.push('Total price is required.');
  }

  const productCounts = {};
  items.forEach((item, index) => {
    const rowLabel = `Row ${index + 1}`;
    if (isBlank(item.productNumber)) {
      blockingErrors.push(`${rowLabel}: product number is required.`);
    }
    if (toNumber(item.quantity) == null) {
      blockingErrors.push(`${rowLabel}: quantity is required.`);
    }
    if (toNumber(item.lineTotal) == null) {
      blockingErrors.push(`${rowLabel}: price is required.`);
    }

    if (isBlank(item.description)) {
      warnings.push({
        field: `items[${index}].description`,
        message: `${rowLabel}: description is missing.`,
        severity: 'info',
      });
    }

    const pn = String(item.productNumber || '').trim();
    if (pn) {
      productCounts[pn] = (productCounts[pn] || 0) + 1;
    }
  });

  Object.entries(productCounts).forEach(([pn, count]) => {
    if (count > 1) {
      warnings.push({
        field: 'items.productNumber',
        message: `Product number “${pn}” appears on ${count} separate rows.`,
        severity: 'info',
      });
    }
  });

  return {
    canSave: blockingErrors.length === 0,
    blockingErrors,
    warnings,
  };
}
